// AgentsHub — Phase 3 consolidated admin Agents page.
//
// Mirror of PlayersHub for the agent (sub-agent of admin) side. Direct
// port of the agent SPA's AgentsHub (agent-frontend/src/pages/AgentsHub.tsx)
// into the admin context. The /people top-nav pill → /people/players →
// /people/players/requests by default, but admin can flip to Agents
// via the outer PeopleHub tab strip (which renders Players|Agents|Bots).
//
// Sub-tab structure (with a live pending-count badge on Requests):
//
//   /people/agents                → redirect → /people/agents/requests
//   /people/agents/requests       → AgentRequestsPanel  (sub-agent QR + queues)
//   /people/agents/list           → AgentsListPanel     (roster)
//
// The sub-agent QR config (admin's QR for direct sub-agents) moved
// here from /finance/settings as part of the Phase 3 dismantling.
// Detail / workflow routes (/people/agents/:id, recharge, etc.) remain
// sibling pages without these sub-tabs.

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import PeopleSegmentBar from '../components/PeopleSegmentBar';

// 2026-05-20 — 30_000 → 60_000 to match the new bottom-nav rhythm
// (AgentBottomNav.tsx PENDING_POLL_MS is now 60s). Two pollers
// hitting the same data shape at different cadences was wasteful.
const COUNT_POLL_MS = 60_000;

export default function AgentsHub() {
  const loc = useLocation();

  // Live count of pending agent recharge + withdrawal requests. Drives
  // the magenta badge on the "Requests" sub-tab.
  const [pending, setPending] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });
  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const [r, w] = await Promise.all([
          adminApi.get<{ total: number }>('/admin/payment-requests/agent-recharge?state=pending&limit=1&offset=0'),
          adminApi.get<{ total: number }>('/admin/payment-requests/agent-withdrawal?state=pending&limit=1&offset=0'),
        ]);
        if (alive) setPending({ recharge: r.total || 0, withdrawal: w.total || 0 });
      } catch { /* keep last good value */ }
    };
    fetchCounts();
    // 2026-05-20 — Skip polls when the tab is hidden (matches all
    // other admin pollers post-429 remediation).
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchCounts();
    }, COUNT_POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [loc.pathname]);
  const totalPending = pending.recharge + pending.withdrawal;

  return (
    <>
    <PeopleSegmentBar current="agents" />
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {/* Header — emerald accent matches the agent SPA's AgentsHub
          (and signals "agents" distinctly from the sky Players page). */}
      <motion.header
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="px-2 lg:px-4"
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30">
            <Icon name="support_agent" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              Agents
            </h1>
            <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 truncate">
              Requests · roster — sub-agent QR + recharge / withdrawal queues + agent management
            </p>
          </div>
        </div>
      </motion.header>

      {/* Sub-tabs — emerald accent for visual differentiation from the
          sky Players page. Requests tab carries the live pending count. */}
      <div className="px-2 lg:px-4">
        <MagentaTabBar
          size="lg"
          accent="emerald"
          items={[
            { to: '/people/agents/requests', label: 'Requests', icon: 'inbox',         matchPrefix: '/people/agents/requests', count: totalPending },
            { to: '/people/agents/list',     label: 'Agents',   icon: 'support_agent', matchPrefix: '/people/agents/list' },
          ]}
        />
      </div>

      {/* Active sub-tab */}
      <div className="px-2 lg:px-4">
        <Outlet />
      </div>
    </div>
    </>
  );
}
